import { WarningAmberRounded } from '@mui/icons-material';
import { Box, Typography } from '@mui/material';
import type { ApexOptions } from 'apexcharts';
import dayjs from 'dayjs';
import { ThemePalette } from 'mui.theme';
import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import ReactApexChart from 'react-apexcharts';
import { useAppSelector } from 'store/hook';
import { isAllTimeLineDataEmpty } from 'utils/driverBehavior';
import '../styles/timeline.css';
import useAppTheme from 'hooks/useAppTheme';
import { convertUtcToDisplayTime } from 'utils/helpers/dateTime';
import useCustomerInfo from 'hooks/useCustomerInfo';
import {
  aggregateSchedulePlanByBumperNumber,
  collapseTravelSegments,
} from 'views/FleetPlanner/utils/dataFormatters';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import useStartEndDates from 'views/EngineHours/hooks/useStartEndDates';
import {
  locationColors,
  shiftColorPalette,
} from 'views/FleetPlanner/utils/constants';
import { type PlanDataType } from '../../types/steps.types';
import { truncateTo2dp } from 'utils/helpers/general';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * Generates the ApexCharts series data for a single asset.
 */
const generateSeriesForAsset = (
  deviceData: PlanDataType[],
  bumperNumber: string
) => {
  const timeLineData: any[] = [];
  if (!Array.isArray(deviceData)) return [];

  deviceData.forEach((item: PlanDataType) => {
    try {
      const start = dayjs.utc(item.start_ts);
      const end = dayjs.utc(item.end_ts);

      if (!start.isValid() || !end.isValid() || start.valueOf() > end.valueOf())
        return;

      let colors = { fill: '#808080', stroke: '#A9A9A9' };
      let actionType = '';

      if (item?.action === 'loading') {
        const locationKey = item?.start_location?.toLowerCase();
        colors = locationColors[locationKey] || colors;
        actionType = 'Loading';
        timeLineData.push({
          x: bumperNumber,
          y: [start.valueOf(), end.valueOf()],
          fillColor: colors.fill,
          strokeColor: colors.stroke,
          extraDetails: {
            ...item,
            action: actionType,
            start: start.valueOf(),
            end: end.valueOf(),
            startLocation: item?.start_location,
            endLocation: item?.end_location,
          },
        });
      } else if (item?.action === 'dumping') {
        colors = {
          fill: 'rgba(220, 38, 38, 0.3)',
          stroke: 'rgba(220, 38, 38, 0.7)',
        };
        actionType = 'Dumping';
        timeLineData.push({
          x: bumperNumber,
          y: [start.valueOf(), end.valueOf()],
          fillColor: colors.fill,
          strokeColor: colors.stroke,
          extraDetails: {
            ...item,
            action: actionType,
            start: start.valueOf(),
            end: end.valueOf(),
            startLocation: item?.start_location,
            endLocation: item?.end_location,
          },
        });
      }
    } catch (innerErr) {
      console.error(`Error parsing item for ${bumperNumber}:`, innerErr);
    }
  });

  return [{ name: '', data: timeLineData }];
};

interface ChartTooltipState {
  visible: boolean;
  x: number;
  y: number;
  title: string;
  eventName: string;
  startTime: string;
  endTime: string;
  color: string;
}

export const AssetWiseTimeLineChart = () => {
  const { t } = useTranslation();
  const { customerTz } = useCustomerInfo();

  const [tooltipState, setTooltipState] = useState<ChartTooltipState>({
    visible: false,
    x: 0,
    y: 0,
    title: '',
    eventName: '',
    startTime: '',
    endTime: '',
    color: '',
  });

  useEffect(() => {
    const hide = () => {
      setTooltipState((prev) => ({ ...prev, visible: false }));
    };
    document.addEventListener('click', hide);
    return () => {
      document.removeEventListener('click', hide);
    };
  }, []);

  const { enableDarkTheme } = useAppTheme();
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;

  const { shiftsWithTime } = useStartEndDates({
    showShiftWise: true,
    startDate: new Date(),
    endDate: new Date(),
  });
  const selectedPlanData = useAppSelector((state) =>
    state.fleetPlanningReducer.selectedPlan === 'user'
      ? state.fleetPlanningReducer.userPlan
      : state.fleetPlanningReducer.optimizedPlan
  );
  const planTimeWindow = useAppSelector(
    (state) => state.fleetPlanningReducer.planTimeWindow
  );

  const { startDateWithTime, endDateWithTime } = useMemo(() => {
    const planStart = planTimeWindow
      ? dayjs.utc(planTimeWindow.startTime).valueOf()
      : null;
    const planEnd = planTimeWindow
      ? dayjs.utc(planTimeWindow.endTime).valueOf()
      : null;

    if (!selectedPlanData.planData || selectedPlanData.planData.length === 0) {
      return {
        startDateWithTime: planStart ?? dayjs.utc().startOf('day').valueOf(),
        endDateWithTime: planEnd ?? dayjs.utc().endOf('day').valueOf(),
      };
    }

    const allTimestamps = selectedPlanData.planData.flatMap((d) => [
      dayjs.utc(d.start_ts).valueOf(),
      dayjs.utc(d.end_ts).valueOf(),
    ]);
    allTimestamps.sort((a, b) => a - b);
    const dataStart = allTimestamps[0];
    const dataEnd = allTimestamps[allTimestamps.length - 1];

    return {
      startDateWithTime: Math.min(planStart ?? dataStart, dataStart),
      endDateWithTime: Math.max(planEnd ?? dataEnd, dataEnd),
    };
  }, [selectedPlanData.planData, planTimeWindow]);

  const { groupedPlanData: aggregatedDataByBumperNumber } = useMemo(() => {
    const collapsed = collapseTravelSegments(selectedPlanData.planData);
    return aggregateSchedulePlanByBumperNumber(collapsed);
  }, [selectedPlanData.planData]);

  const baseChartOptions = useMemo<ApexOptions>(() => {
    let annotations: ApexOptions['annotations'];
    if (shiftsWithTime && shiftsWithTime.length > 0) {
      annotations = {
        xaxis: shiftsWithTime.map((shift, index) => ({
          x: new Date(shift.startDate).getTime(),
          x2: new Date(shift.endDate).getTime(),
          borderColor: shiftColorPalette[index % shiftColorPalette.length],
          fillColor: shiftColorPalette[index % shiftColorPalette.length],
          opacity: 0.1,
          label: {
            borderWidth: 0,
            position: 'top',
            orientation: 'vertical',
            textAnchor: 'middle',
            offsetX: -5,
            offsetY: 0,
            style: {
              color: '#FFFFFF',
              background: '#1E293B',
              fontSize: '10px',
              fontWeight: 600,
              padding: {
                left: 3,
                right: 3,
                top: 3,
                bottom: 3,
              },
            },
          },
        })),
      };
    }

    return {
      chart: {
        type: 'rangeBar',
        background: 'transparent',
        animations: { enabled: false },
        toolbar: {
          show: false,
          tools: { download: false },
        },
        zoom: {
          enabled: false,
        },
        selection: {
          enabled: false,
        },
        brush: {
          enabled: false,
        },
      },
      plotOptions: {
        bar: { horizontal: true, barHeight: '70%', borderRadius: 2 },
      },
      states: { hover: { filter: { type: 'darken', value: 0.1 } } },
      colors: shiftColorPalette,
      xaxis: {
        type: 'datetime',
        min: startDateWithTime,
        max: endDateWithTime,
        labels: {
          datetimeUTC: true,
          style: {
            colors: palette.textPrimary,
          },
          formatter: (value: any) => {
            return dayjs.utc(Number(value)).tz(customerTz).format('HH:mm');
          },
        },
      },
      yaxis: {
        labels: {
          style: {
            colors: palette.textPrimary,
          },
        },
      },
      grid: {
        yaxis: { lines: { show: false } },
        padding: { left: 35 },
      },
      stroke: { width: 1 },
      fill: { type: 'solid', opacity: 0.7 },
      annotations,
    };
  }, [
    shiftsWithTime,
    enableDarkTheme,
    customerTz,
    startDateWithTime,
    endDateWithTime,
  ]);

  const masterChartOptions = useMemo<ApexOptions>(
    () => ({
      ...baseChartOptions,
      chart: {
        ...baseChartOptions.chart,
        id: 'masterTimeline',
      },
      yaxis: { show: false },
      grid: { ...baseChartOptions.grid, yaxis: { lines: { show: false } } },
    }),
    [baseChartOptions]
  );

  const assetChartOptions = useMemo<ApexOptions>(
    () => ({
      ...baseChartOptions,
      chart: {
        ...baseChartOptions.chart,
        sparkline: { enabled: true },
        events: {
          dataPointMouseEnter: (event: MouseEvent, _ctx: any, config: any) => {
            const { seriesIndex, dataPointIndex, w } = config;
            const dataPoint =
              w.globals.initialSeries[seriesIndex]?.data[dataPointIndex];
            if (!dataPoint) return;
            const seriesName = w.globals.initialSeries[seriesIndex]?.name;
            const displayEventName = dataPoint.customEventType || seriesName;
            const startLoc = dataPoint.extraDetails?.startLocation;
            const location = /^[dD]/.test(startLoc)
              ? 'Dumping'
              : `Loading Site ${startLoc}`;
            const fmt = (ts: number) =>
              convertUtcToDisplayTime(
                ts,
                'configured',
                customerTz,
                'DD/MM/YYYY HH:mm:ss'
              );
            setTooltipState({
              visible: true,
              x: event.clientX,
              y: event.clientY,
              title: String(dataPoint.x),
              eventName: `${displayEventName} ${location}`,
              startTime: fmt(dataPoint.extraDetails?.start),
              endTime: fmt(dataPoint.extraDetails?.end),
              color: w.globals.colors[seriesIndex] ?? '#4ade80',
            });
          },
          dataPointMouseLeave: () => {
            setTooltipState((prev) => ({ ...prev, visible: false }));
          },
          mouseLeave: () => {
            setTooltipState((prev) => ({ ...prev, visible: false }));
          },
        },
      },
      tooltip: { enabled: false },
      xaxis: {
        ...baseChartOptions.xaxis,
        labels: { show: false },
        axisTicks: { show: false },
        axisBorder: { show: false },
      },
      yaxis: { show: false },
      grid: { ...baseChartOptions.grid, yaxis: { lines: { show: false } } },
      legend: { show: false },
    }),
    [baseChartOptions, customerTz, setTooltipState]
  );

  const allData = useMemo(
    () => [{ data: Object.values(aggregatedDataByBumperNumber).flat() }],
    [aggregatedDataByBumperNumber]
  );

  return (
    <>
      <Box
        sx={{
          borderRadius: 2,
          backgroundColor: enableDarkTheme ? 'rgba(38, 79, 225, 0.1)' : '#fff',
          padding: '0px 16px 32px',
          border: enableDarkTheme ? '1px solid #334155' : 'none',
        }}
      >
        {isAllTimeLineDataEmpty(allData) ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            height="100%"
          >
            <WarningAmberRounded />
            <Typography
              sx={{
                color: palette.textPrimary,
                fontSize: '14px',
                ml: 1,
              }}
            >
              {t('no_timeline_data_available')}
            </Typography>
          </Box>
        ) : (
          <>
            <Box
              sx={{
                display: 'flex',
                gap: 2,
                height: '55px',
              }}
            >
              <Box display={'flex'} alignItems={'center'} gap={'8px'}>
                <Box display={'flex'} alignItems={'center'} gap={'4px'}>
                  <span
                    style={{
                      width: 14,
                      height: 14,
                      borderRadius: '50%',
                      background: locationColors.l0.fill,
                      border: `1px solid ${locationColors.l0.stroke}`,
                    }}
                  />
                  <span style={{ fontSize: '12px' }}>
                    {t('loading_site_l0')}
                  </span>
                </Box>
                <Box display={'flex'} alignItems={'center'} gap={'4px'}>
                  <span
                    style={{
                      width: 14,
                      height: 14,
                      borderRadius: '50%',
                      background: locationColors.l1.fill,
                      border: `1px solid ${locationColors.l1.stroke}`,
                    }}
                  />
                  <span style={{ fontSize: '12px' }}>
                    {t('loading_site_l1')}
                  </span>
                </Box>
              </Box>
              <Box display={'flex'} alignItems={'center'} gap={'4px'}>
                <span
                  style={{
                    width: 14,
                    height: 14,
                    borderRadius: '50%',
                    background: 'rgba(220, 38, 38, 0.7)',
                  }}
                />
                <span style={{ fontSize: '12px' }}>{t('dumping')}</span>
              </Box>
            </Box>

            <Box sx={{ paddingLeft: '159px', paddingRight: '16px' }} mt={-3}>
              <ReactApexChart
                options={masterChartOptions}
                series={[{ data: [] }]}
                type="rangeBar"
                height={60}
                key="master-timeline"
              />
            </Box>

            <Box sx={{ mt: '-20px', maxHeight: '400px', overflowY: 'auto' }}>
              {Object.entries(aggregatedDataByBumperNumber).map(
                ([bumperNumber, deviceData]) => {
                  const totalTrips = deviceData.filter(
                    (r) => r.action === 'dumping'
                  ).length;
                  // API plan data stores material_dumped on loading rows; sum all rows since
                  // only loading or dumping rows ever carry a non-zero value per trip.
                  const totalTons = deviceData.reduce(
                    (sum, r) => sum + (Number(r.material_dumped) || 0),
                    0
                  );
                  const assetSeries = generateSeriesForAsset(
                    deviceData,
                    bumperNumber
                  );

                  return (
                    <Box
                      key={bumperNumber}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        borderTop: `1px solid ${
                          enableDarkTheme ? '#334155' : '#E2E8F0'
                        }`,
                      }}
                    >
                      <Box
                        sx={{
                          width: '160px',
                          flexShrink: 0,
                          padding: '4px',
                          fontSize: '12px',
                        }}
                      >
                        <Typography
                          variant="subtitle1"
                          sx={{
                            fontWeight: '600',
                            fontSize: '0.9rem',
                            mb: 0.5,
                            color: palette.textPrimary,
                          }}
                        >
                          {bumperNumber}
                        </Typography>

                        <Box
                          display="grid"
                          gridTemplateColumns="repeat(2, 1fr)"
                          gap={0.5}
                          sx={{
                            textAlign: 'left',
                            color: palette.textPrimary,
                          }}
                        >
                          <Box>
                            <Typography
                              color="text.secondary"
                              fontSize="0.75rem"
                            >
                              {t('total_trips')}
                            </Typography>
                            <Typography fontWeight="600" variant="subtitle2">
                              {totalTrips}
                            </Typography>
                          </Box>

                          <Box>
                            <Typography
                              color="text.secondary"
                              fontSize="0.75rem"
                            >
                              {t('total_tons')}
                            </Typography>
                            <Typography fontWeight="600" variant="subtitle2">
                              {truncateTo2dp(totalTons)}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>

                      <Box
                        sx={{
                          flexGrow: 1,
                          paddingRight: '16px',
                          overflow: 'visible',
                          '& .apexcharts-canvas': { overflow: 'visible' },
                          '& .apexcharts-svg': { overflow: 'visible' },
                        }}
                      >
                        <ReactApexChart
                          options={assetChartOptions}
                          series={assetSeries}
                          type="rangeBar"
                          height={80}
                          key={`asset-${bumperNumber}`}
                        />
                      </Box>
                    </Box>
                  );
                }
              )}
            </Box>
          </>
        )}
      </Box>
      {tooltipState.visible &&
        createPortal(
          <Box
            sx={{
              position: 'fixed',
              left: tooltipState.x + 14,
              top: tooltipState.y - 14,
              zIndex: 9999,
              background: '#2E3B4E',
              color: '#fff',
              padding: '12px',
              borderRadius: '8px',
              fontFamily: 'Arial, sans-serif',
              boxShadow: '0px 4px 8px rgba(0,0,0,0.2)',
              pointerEvents: 'none',
              minWidth: '220px',
            }}
          >
            <Typography
              fontWeight="bold"
              fontSize="14px"
              textAlign="center"
              mb={1}
              color="white"
            >
              {tooltipState.title}
            </Typography>
            <Typography
              fontSize="12px"
              mb={0.5}
              sx={{ textTransform: 'capitalize' }}
            >
              <Box
                component="span"
                sx={{ color: tooltipState.color, fontWeight: 'bold' }}
              >
                Event:
              </Box>{' '}
              {tooltipState.eventName}
            </Typography>
            <Typography fontSize="12px" mb={0.5}>
              <Box
                component="span"
                sx={{ color: tooltipState.color, fontWeight: 'bold' }}
              >
                Start:
              </Box>{' '}
              {tooltipState.startTime}
            </Typography>
            <Typography fontSize="12px">
              <Box
                component="span"
                sx={{ color: tooltipState.color, fontWeight: 'bold' }}
              >
                End:
              </Box>{' '}
              {tooltipState.endTime}
            </Typography>
          </Box>,
          document.body
        )}
    </>
  );
};
